/**
 * "New Rentals" (linked from the header nav, previously inert) — the
 * newest listings, grouped by city. `GET /api/listings` already comes back
 * newest-first (modules/public/service.ts orders by publishedAt desc), so
 * grouping by first-appearance order — not alphabetical — is what makes
 * "the city with the newest listing overall" the first section, matching
 * the page's own premise without needing a timestamp exposed to the client
 * at all: the array's existing order already encodes it.
 */

import { useEffect, useState } from 'react';
import type { PublicListingDto } from 'shared';
import { api } from '../../lib/api.js';
import { NewRentalsCard } from './NewRentalsCard.js';

function groupByCity(listings: PublicListingDto[]): [string, PublicListingDto[]][] {
  const groups = new Map<string, PublicListingDto[]>();
  for (const listing of listings) {
    const key = listing.city || 'Other areas';
    const group = groups.get(key);
    if (group) {
      group.push(listing);
    } else {
      groups.set(key, [listing]);
    }
  }
  return [...groups.entries()];
}

export function NewRentals() {
  const [listings, setListings] = useState<PublicListingDto[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void api<{ data: PublicListingDto[] }>('/listings')
      .then((response) => {
        if (!cancelled) setListings(response.data);
      })
      .catch(() => {
        if (!cancelled) setError('Could not load current listings. Please try again shortly.');
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const groups = listings ? groupByCity(listings) : [];

  return (
    <div className="new-rentals">
      <div className="new-rentals__intro">
        <p className="public__eyebrow">Fresh on the market</p>
        <h1>New Rentals</h1>
        <p className="muted">
          Our most recently published listings, newest first — grouped by where they are.
        </p>
      </div>

      {error ? <p className="alert alert--error">{error}</p> : null}
      {listings === null && !error ? <p className="muted">Loading listings…</p> : null}
      {listings !== null && listings.length === 0 ? (
        <p className="muted">No listings are published yet — check back soon.</p>
      ) : null}

      {groups.map(([city, cityListings]) => (
        <section key={city} className="city-section">
          <div className="city-section__header">
            <h2>{city}</h2>
            <span className="city-section__count">
              {cityListings.length} {cityListings.length === 1 ? 'new rental' : 'new rentals'}
            </span>
          </div>
          <div className="showcase-grid">
            {cityListings.map((listing) => (
              <NewRentalsCard key={listing.id} listing={listing} />
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}
